import { roomLib } from "@agentcall/core/lib"
import { zodResolver } from "@hookform/resolvers/zod"
import { X } from "lucide-react"
import { Suspense, useMemo, useState } from "react"
import { useForm } from "react-hook-form"
import { z } from "zod"
import type { RoomModels } from "@/api/client/room"
import { roomApi } from "@/api/client/room"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { ReactErrorBoundary } from "@/components/ui/error-boundary"
import { Input } from "@/components/ui/input"
import {
  Pagination,
  PaginationContent,
  PaginationFirst,
  PaginationItem,
  PaginationLast,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination"
import { Spinner } from "@/components/ui/spinner/Spinner"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"

// 検索フォームのスキーマ
const searchFormSchema = z.object({
  propertyNameLike: z.string().optional(),
  nameLike: z.string().optional(),
  propertyAddressLike: z.string().optional(),
  ownerNameLike: z.string().optional(),
})

type SearchFormValues = z.infer<typeof searchFormSchema>

// 検索結果の型
export interface RoomSearchResult {
  id: string
  name: string
  propertyName: string
  tenantName: string | null
  propertyEchoesOwnerId: string | null
}

// 選択結果の型
export interface SelectedRoom {
  id: string
  name: string
  propertyName: string
  propertyEchoesOwnerId: string | null
}

interface RoomSearchModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSelect: (room: SelectedRoom) => void
}

const ITEMS_PER_PAGE = 10

export function RoomSearchModal({ open, onOpenChange, onSelect }: RoomSearchModalProps) {
  const [isSearched, setIsSearched] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [searchParams, setSearchParams] = useState<SearchFormValues | null>(null)

  const form = useForm<SearchFormValues>({
    resolver: zodResolver(searchFormSchema),
    defaultValues: {
      propertyNameLike: "",
      nameLike: "",
      propertyAddressLike: "",
      ownerNameLike: "",
    },
  })

  const handleSearch = (values: SearchFormValues) => {
    setSearchParams(values)
    setCurrentPage(1)
    setIsSearched(true)
  }

  const handlePageChange = (page: number) => {
    setCurrentPage(page)
  }

  const handleReset = () => {
    form.reset()
    setIsSearched(false)
    setCurrentPage(1)
    setSearchParams(null)
  }

  const handleSelectRoom = (result: RoomSearchResult) => {
    const selectedData: SelectedRoom = {
      id: result.id,
      name: result.name,
      propertyName: result.propertyName,
      propertyEchoesOwnerId: result.propertyEchoesOwnerId,
    }
    onSelect(selectedData)
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[1148px] max-h-[90vh] overflow-y-auto p-0">
        <div className="p-[25px]">
          <DialogHeader className="mb-5">
            <DialogTitle className="text-[18px] font-semibold text-[#3f4652]">部屋検索</DialogTitle>
          </DialogHeader>

          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="absolute right-[14px] top-[14px] rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100"
          >
            <X className="h-[18px] w-[18px] text-[#354267]" />
            <span className="sr-only">閉じる</span>
          </button>

          <div className="space-y-[30px]">
            <div className="border border-slate-200 rounded-[10px]">
              <form
                onSubmit={form.handleSubmit(handleSearch)}
                className="py-[25px] w-[434px] mx-auto"
              >
                <div className="space-y-[15px]">
                  <div className="flex items-center justify-between">
                    <label
                      htmlFor="propertyNameLike"
                      className="text-xs font-semibold text-[#3f4652] w-[84px]"
                    >
                      物件名
                    </label>
                    <div className="w-[350px]">
                      <Input id="propertyNameLike" {...form.register("propertyNameLike")} />
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <label
                      htmlFor="nameLike"
                      className="text-xs font-semibold text-[#3f4652] w-[84px]"
                    >
                      部屋番号
                    </label>
                    <div className="w-[350px]">
                      <Input id="nameLike" {...form.register("nameLike")} />
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <label
                      htmlFor="propertyAddressLike"
                      className="text-xs font-semibold text-[#3f4652] w-[84px]"
                    >
                      物件住所
                    </label>
                    <div className="w-[350px]">
                      <Input id="propertyAddressLike" {...form.register("propertyAddressLike")} />
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <label
                      htmlFor="ownerNameLike"
                      className="text-xs font-semibold text-[#3f4652] w-[84px]"
                    >
                      オーナー名
                    </label>
                    <div className="w-[350px]">
                      <Input id="ownerNameLike" {...form.register("ownerNameLike")} />
                    </div>
                  </div>
                </div>

                <div className="flex justify-center gap-5 mt-5">
                  <Button type="submit" variant="default">
                    検索する
                  </Button>
                  <Button type="button" variant="outline" onClick={handleReset}>
                    検索をリセット
                  </Button>
                </div>
              </form>
            </div>

            {isSearched && (
              <ReactErrorBoundary>
                <Suspense fallback={<RoomSearchModalFallback />}>
                  <RoomSearchModalContent
                    searchParams={searchParams}
                    currentPage={currentPage}
                    onPageChange={handlePageChange}
                    onSelectRoom={handleSelectRoom}
                  />
                </Suspense>
              </ReactErrorBoundary>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

interface RoomSearchModalContentProps {
  searchParams: SearchFormValues | null
  currentPage: number
  onPageChange: (page: number) => void
  onSelectRoom: (result: RoomSearchResult) => void
}

function RoomSearchModalContent({
  searchParams,
  currentPage,
  onPageChange,
  onSelectRoom,
}: RoomSearchModalContentProps) {
  const apiParams = useMemo(() => {
    const params: RoomModels.RoomListParams = {
      page: currentPage,
      limit: ITEMS_PER_PAGE,
      withProperty: "true",
      withPropertyOwner: "true",
      withTenants: "true",
    }

    if (searchParams?.propertyNameLike) {
      params.propertyNameLike = searchParams.propertyNameLike
    }
    if (searchParams?.nameLike) {
      params.nameLike = searchParams.nameLike
    }
    if (searchParams?.propertyAddressLike) {
      params.propertyAddressLike = searchParams.propertyAddressLike
    }
    if (searchParams?.ownerNameLike) {
      params.ownerNameLike = searchParams.ownerNameLike
    }

    return params
  }, [searchParams, currentPage])

  const { data } = roomApi.useRoomListSuspense(apiParams)

  // APIレスポンスをRoomSearchResultに変換
  const searchResults: RoomSearchResult[] = useMemo(() => {
    return data.rooms.map((room) => {
      const currentTenant = roomLib.roomActiveTenant(room)
      return {
        id: String(room.id),
        name: room.name,
        propertyName: room.property?.name ?? "-",
        tenantName: currentTenant?.name ?? null,
        propertyEchoesOwnerId: room.property?.owner?.echoesOwnerId ?? null,
      }
    })
  }, [data.rooms])

  const totalCount = data.total
  const totalPages = Math.ceil(totalCount / ITEMS_PER_PAGE)

  // ページ番号の配列を生成
  const getPageNumbers = () => {
    const pages: number[] = []
    const maxVisiblePages = 5
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2))
    const endPage = Math.min(totalPages, startPage + maxVisiblePages - 1)

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1)
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i)
    }
    return pages
  }

  return (
    <div className="space-y-4">
      <div className="rounded-[10px] overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-white border border-slate-200">
              <TableHead className="whitespace-nowrap text-xs font-semibold text-[#3f4652] h-8 px-4">
                部屋ID
              </TableHead>
              <TableHead className="whitespace-nowrap text-xs font-semibold text-[#3f4652] h-8 px-4">
                物件名
              </TableHead>
              <TableHead className="whitespace-nowrap text-xs font-semibold text-[#3f4652] h-8 px-4">
                部屋番号
              </TableHead>
              <TableHead className="whitespace-nowrap text-xs font-semibold text-[#3f4652] h-8 px-4">
                現在の賃借人
              </TableHead>
              <TableHead className="whitespace-nowrap text-xs font-semibold text-[#3f4652] h-8 px-4">
                案件へ適用
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {searchResults.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-8 text-slate-400">
                  検索結果がありません
                </TableCell>
              </TableRow>
            ) : (
              searchResults.map((result) => (
                <TableRow key={result.id} className="border border-slate-200">
                  <TableCell className="text-xs font-medium text-[#3f4652] py-2 px-4">
                    {result.id}
                  </TableCell>
                  <TableCell className="text-xs font-medium text-[#3f4652] py-2 px-4">
                    {result.propertyName}
                  </TableCell>
                  <TableCell className="text-xs font-medium text-[#3f4652] py-2 px-4">
                    {result.name}
                  </TableCell>
                  <TableCell className="text-xs font-medium text-[#3f4652] py-2 px-4">
                    {result.tenantName ?? "未入居"}
                  </TableCell>
                  <TableCell className="py-2 px-4">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={Boolean(result.propertyEchoesOwnerId)}
                      title={
                        result.propertyEchoesOwnerId
                          ? "Echoes連携物件の部屋は賃借人追加に選択できません"
                          : undefined
                      }
                      onClick={() => onSelectRoom(result)}
                      className="text-xs font-bold"
                    >
                      この内容で適用
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-xs text-slate-500">
            {totalCount}件中 {(currentPage - 1) * ITEMS_PER_PAGE + 1}-
            {Math.min(currentPage * ITEMS_PER_PAGE, totalCount)}
            件を表示
          </p>
          <Pagination>
            <PaginationContent>
              <PaginationItem>
                <PaginationFirst onClick={() => onPageChange(1)} disabled={currentPage === 1} />
              </PaginationItem>
              <PaginationItem>
                <PaginationPrevious
                  onClick={() => onPageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                />
              </PaginationItem>
              {getPageNumbers().map((page) => (
                <PaginationItem key={page}>
                  <PaginationLink
                    onClick={() => onPageChange(page)}
                    isActive={page === currentPage}
                    className="h-8 w-8 cursor-pointer"
                  >
                    {page}
                  </PaginationLink>
                </PaginationItem>
              ))}
              <PaginationItem>
                <PaginationNext
                  onClick={() => onPageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                />
              </PaginationItem>
              <PaginationItem>
                <PaginationLast
                  onClick={() => onPageChange(totalPages)}
                  disabled={currentPage === totalPages}
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        </div>
      )}
    </div>
  )
}

function RoomSearchModalFallback() {
  return (
    <div className="p-10 flex flex-col items-center justify-center">
      <Spinner className={"text-slate-600 size-10 transition-opacity"} />
    </div>
  )
}
